/**
 * The full-results view Enter opens from the header search when nothing is
 * highlighted (agora#269): every row a section's own text search returns,
 * not the header panel's own capped preview (`model.ts`'s `runSearch` caps
 * each source at 20). Mail (`mail/MailSearchResults.tsx`), Calendar
 * (`calendar/CalendarSearchResults.tsx`) and Chat (`ChatSearchResults.tsx`)
 * each drive this with their own client and search call; this only draws
 * what comes back, and only ever renders one source's rows -- there is no
 * cross-source grouping here the way the header panel has.
 *
 * A real page, not the header panel's virtual-focus combobox: each row is a
 * genuine `<button>` in the tab order, reusing `search.css`'s own
 * `.search-row` classes (unscoped there, so they draw the same here) rather
 * than a second stylesheet for what is visually the same row.
 */
import { Empty, Icon, type IconName } from '@wtfalch/design';
import { formatMessageWhen, formatWhen } from '../ui/format.ts';
import type { SearchRow } from './types.ts';

export function SearchResultsPage({
  query,
  icon,
  loading,
  error,
  rows,
  emptyLabel,
  onOpen,
}: {
  query: string;
  icon: IconName;
  loading: boolean;
  error: string | null;
  rows: readonly SearchRow[];
  emptyLabel: string;
  onOpen: (row: SearchRow) => void;
}) {
  return (
    <div className="search-results h-full overflow-y-auto p-4">
      <h2 className="m-0 mb-2 text-text text-sm font-strong">Results for “{query}”</h2>
      {error ? (
        <p className="m-0 py-2 px-3 text-bad text-xs">{error}</p>
      ) : loading && rows.length === 0 ? (
        <p className="m-0 p-3 text-muted text-sm">Searching…</p>
      ) : rows.length === 0 ? (
        <Empty icon={icon}>{emptyLabel}</Empty>
      ) : (
        <ul className="flex flex-col m-0 p-0 list-none">
          {rows.map((row) => (
            <li key={row.id}>
              <button
                type="button"
                className="search-row search-results-row"
                onClick={() => onOpen(row)}
              >
                <span className="flex flex-none text-muted" aria-hidden="true">
                  <Icon name={icon} size={16} />
                </span>
                <span className="flex flex-1 flex-col min-w-0 gap-0.5">
                  <span className="truncate text-text text-sm font-strong">{row.title}</span>
                  {row.snippet && (
                    <span className="truncate text-muted text-xs">{row.snippet}</span>
                  )}
                </span>
                {row.time && (
                  <span className="flex-none text-muted text-2xs whitespace-nowrap">
                    {row.dated ? formatMessageWhen(row.time) : formatWhen(row.time)}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
